/**
 * Quick-action control to send a combined Video Bot + MCQ + Tech Assessment
 * invite (only stages present in the candidate's pipeline).
 */
import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Send, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

import { v1Post } from '@/lib/api';
import { toastEmailDispatch } from '@/lib/emailDispatchToast';
import { useHasPermission } from '@/auth/permissions';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const STAGE_META = [
  { pipeline: 'videobot', code: 'VIDEOBOT', label: 'Video Bot' },
  { pipeline: 'mcq', code: 'MCQ', label: 'IQ Assessment' },
  { pipeline: 'tech_assessment', code: 'TECH_ASSESSMENT', label: 'Tech Assessment' },
];

export function eligibleCombinedStages(pipelineStageSet) {
  const set = pipelineStageSet instanceof Set
    ? pipelineStageSet
    : new Set(pipelineStageSet || []);
  return STAGE_META.filter((s) => set.has(s.pipeline));
}

export function CombinedAssessmentInviteButton({
  candidateId,
  evaluationId,
  pipelineStageSet,
  disabled = false,
  onSent,
  assessmentReadiness,
}) {
  const queryClient = useQueryClient();
  const canEditEvaluation = useHasPermission('evaluation.edit');
  const [open, setOpen] = useState(false);
  const [expiry, setExpiry] = useState(72);
  const [sending, setSending] = useState(false);

  const stages = useMemo(
    () => eligibleCombinedStages(pipelineStageSet),
    [pipelineStageSet],
  );

  const stageApiCodes = useMemo(
    () => stages.map((s) => s.code),
    [stages],
  );

  const combinedWarning = assessmentReadiness?.getCombinedWarning?.(stageApiCodes) ?? null;
  const combinedReady = !combinedWarning && !assessmentReadiness?.isLoading;

  if (!canEditEvaluation || !stages.length) return null;

  const send = async () => {
    if (!evaluationId) {
      toast.error('Select an evaluation before sending a combined assessment.');
      return;
    }
    setSending(true);
    try {
      const result = await v1Post(
        `/candidates/${candidateId}/combined-assessment-invite`,
        {
          evaluation_id: evaluationId,
          expires_in_hours: expiry,
        },
      );
      setOpen(false);
      await queryClient.invalidateQueries({
        queryKey: ['v1-candidate-links', candidateId, evaluationId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['v1-combined-invite-status', candidateId, evaluationId],
      });
      onSent?.(result);
      const labels = (result?.stages || []).join(', ') || stages.map((s) => s.label).join(', ');
      toastEmailDispatch(result?.email_dispatch, {
        sentMessage: `Combined assessment invite sent (${labels}).`,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send combined assessment');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5"
        disabled={disabled || !evaluationId || !combinedReady}
        onClick={() => setOpen(true)}
        data-testid="send-combined-assessment"
      >
        <Send className="h-3.5 w-3.5" /> Send combined assessment
      </Button>

      <Dialog open={open} onOpenChange={(v) => { if (!sending) setOpen(v); }}>
        <DialogContent data-testid="combined-assessment-dialog">
          <DialogHeader>
            <DialogTitle>Send combined assessment</DialogTitle>
            <DialogDescription>
              Creates a separate invitation link for each included stage and emails
              them together in one message. Existing per-stage Send buttons are unchanged.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Included stages</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm" data-testid="combined-assessment-stages">
                {stages.map((s) => (
                  <li key={s.code}>{s.label}</li>
                ))}
              </ul>
            </div>
            {combinedWarning && (
              <div
                className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/40 p-3 text-xs text-amber-900"
                data-testid="combined-assessment-config-warning"
              >
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{combinedWarning}</span>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground">Link expiry</label>
              <Select value={String(expiry)} onValueChange={(v) => setExpiry(Number(v))}>
                <SelectTrigger data-testid="combined-link-expiry-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24">24 hours</SelectItem>
                  <SelectItem value="48">48 hours</SelectItem>
                  <SelectItem value="72">72 hours</SelectItem>
                  <SelectItem value="168">7 days</SelectItem>
                  <SelectItem value="336">14 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" disabled={sending} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={send}
              disabled={sending || !combinedReady}
              data-testid="combined-assessment-confirm"
            >
              {sending ? 'Sending…' : 'Generate & send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
